import { useQuery } from '@tanstack/react-query';
import { getAbiItem, type Address, type Hex } from 'viem';
import { usePublicClient, useReadContracts } from 'wagmi';
import { syntraTimelockAbi } from '@syntra/abi';
import { DEPLOYMENT as D } from '../config/env.ts';
import { pagedLogs } from '../lib/logs.ts';

export interface TimelockCall {
  target: Address;
  data: Hex;
}

/** One scheduled operation: one call (`schedule`) or several in index order (`scheduleBatch`). */
export interface TimelockOp {
  id: Hex;
  calls: TimelockCall[];
  predecessor: Hex;
  salt: Hex;
  delay: bigint;
  /** TimelockController.OperationState: 0 unset (cancelled), 1 waiting, 2 ready, 3 done. */
  state: number;
  /** When it becomes executable (unix seconds); 1 once done, 0 when cancelled. */
  timestamp: number;
}

/** Every operation ever scheduled on the timelock, from its events, with each one's state read live. */
export function useTimelockOps() {
  const client = usePublicClient();
  const ops = useQuery({
    queryKey: ['timelock-ops'],
    refetchInterval: 15_000,
    enabled: Boolean(client),
    queryFn: async () => {
      const from = BigInt(D.deployedAtBlock ?? 0);
      const head = await client!.getBlockNumber();
      const [scheduled, salts] = await Promise.all([
        pagedLogs(head, from, (fromBlock, toBlock) =>
          client!.getLogs({ address: D.timelock, event: getAbiItem({ abi: syntraTimelockAbi, name: 'CallScheduled' }), fromBlock, toBlock }),
        ),
        pagedLogs(head, from, (fromBlock, toBlock) =>
          client!.getLogs({ address: D.timelock, event: getAbiItem({ abi: syntraTimelockAbi, name: 'CallSalt' }), fromBlock, toBlock }),
        ),
      ]);
      const saltOf = new Map(salts.map((l) => [l.args.id, l.args.salt]));
      const byId = new Map<Hex, Omit<TimelockOp, 'state' | 'timestamp'>>();
      for (const l of [...scheduled].sort((x, y) => Number(x.args.index! - y.args.index!))) {
        const id = l.args.id!;
        const op = byId.get(id) ?? { id, calls: [], predecessor: l.args.predecessor!, delay: l.args.delay!, salt: (saltOf.get(id) ?? `0x${'0'.repeat(64)}`) as Hex };
        op.calls.push({ target: l.args.target!, data: l.args.data! });
        byId.set(id, op);
      }
      return [...byId.values()];
    },
  });
  const list = ops.data ?? [];
  const states = useReadContracts({
    allowFailure: false,
    query: { enabled: list.length > 0, refetchInterval: 10_000 },
    contracts: list.flatMap((o) => [
      { address: D.timelock, abi: syntraTimelockAbi, functionName: 'getOperationState', args: [o.id] } as const,
      { address: D.timelock, abi: syntraTimelockAbi, functionName: 'getTimestamp', args: [o.id] } as const,
    ]),
  });
  const all: TimelockOp[] = list.map((o, i) => ({
    ...o,
    state: Number(states.data?.[i * 2] ?? 1),
    timestamp: Number(states.data?.[i * 2 + 1] ?? 0n),
  }));
  return { ops: all, isLoading: ops.isPending || (list.length > 0 && states.isPending) };
}
